/**
 * HistoryCard — one row in the history list.
 *
 * The card body and row actions stay as sibling Pressables so React Native Web
 * does not render nested <button> elements.
 */

import { Image, Pressable, StyleSheet, Text, View } from 'react-native';

import { AppIcon } from '@/components/app-icon';
import { Colors, Radii, Spacing } from '@/constants/theme';
import { historyStatusSentence, isPendingPrint } from '@/lib/history-presenter';
import { activityIcon } from '@/lib/icons';
import type { HistoryItem } from '@/types/history';

interface HistoryCardProps {
  item: HistoryItem;
  todayIso: string;
  onPress: (item: HistoryItem) => void;
  onPrint?: (item: HistoryItem) => void;
}

export function HistoryCard({ item, todayIso, onPress, onPrint }: HistoryCardProps) {
  const statusSentence = historyStatusSentence(item, todayIso);
  const pending = isPendingPrint(item);

  return (
    <View style={styles.card}>
      <Pressable
        onPress={() => onPress(item)}
        style={({ pressed }) => [styles.cardMain, pressed && styles.cardPressed]}
        accessibilityRole="button"
        accessibilityLabel={`Buka worksheet ${item.name}. ${statusSentence}`}
      >
        <View style={styles.thumb}>
          {item.imageUrl ?? item.thumbnailUrl ? (
            <Image
              source={{ uri: (item.imageUrl ?? item.thumbnailUrl)! }}
              style={styles.thumbImage}
              resizeMode="cover"
            />
          ) : (
            <AppIcon source={activityIcon(item.activityLabel)} size={40} />
          )}
        </View>

        <View style={styles.content}>
          <Text style={styles.title} numberOfLines={2}>
            {item.name}
          </Text>
          <Text style={styles.status}>{statusSentence}</Text>
          {item.temaNames[0] ? (
            <Text style={styles.tema} numberOfLines={1}>{item.temaNames[0]}</Text>
          ) : null}
        </View>
        <View style={styles.chevron} accessibilityElementsHidden />
      </Pressable>

      {pending && onPrint ? (
        <Pressable
          onPress={() => onPrint(item)}
          style={({ pressed }) => [styles.actionButton, pressed && styles.actionPressed]}
          accessibilityRole="button"
          accessibilityLabel={`Cetak worksheet ${item.name}`}
        >
          <Text style={styles.printActionText}>Cetak</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.three,
  },
  cardMain: {
    alignItems: 'center',
    borderRadius: Radii.small,
    flex: 1,
    flexDirection: 'row',
    gap: Spacing.three,
    minWidth: 0,
    paddingVertical: Spacing.one,
  },
  cardPressed: {
    backgroundColor: Colors.light.background,
  },
  thumb: {
    alignItems: 'center',
    backgroundColor: Colors.light.background,
    borderRadius: Radii.small,
    height: 64,
    justifyContent: 'center',
    overflow: 'hidden',
    width: 64,
  },
  thumbImage: {
    height: '100%',
    width: '100%',
  },
  content: {
    flex: 1,
    gap: Spacing.one,
    justifyContent: 'center',
    minWidth: 0,
  },
  title: {
    color: Colors.light.text,
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 22,
  },
  status: {
    color: Colors.light.textSecondary,
    fontSize: 12,
    lineHeight: 17,
  },
  tema: {
    color: Colors.light.textSecondary,
    fontSize: 12,
    lineHeight: 17,
  },
  chevron: {
    borderBottomWidth: 2,
    borderColor: Colors.light.textSecondary,
    borderRightWidth: 2,
    height: 8,
    marginLeft: Spacing.one,
    transform: [{ rotate: '-45deg' }],
    width: 8,
  },
  actionButton: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
    minWidth: 48,
    paddingHorizontal: Spacing.one,
  },
  actionPressed: {
    opacity: 0.65,
  },
  printActionText: {
    color: Colors.light.primary,
    fontSize: 13,
    fontWeight: '700',
  },
});
